import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { drawLine } from './draw-fns'
import { cupUnits, deliveryAddressLines, guestInitials } from './slipText'
import { optionLines } from './tableTicket'
import {
  buildCupLabelLines,
  CupLabelFontScale,
  CupLabelLine,
  CupLabelTemplate,
  findCupLabelTemplate,
  normalizeCupLabelTemplate,
} from './cupLabelTemplate'

function finishCanvas(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, y: number) {
  const data = ctx.getImageData(0, 0, 576, Math.max(y, 32))
  canvas.height = Math.max(y - 16, 32)
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.putImageData(data, 0, 0)
  return canvas
}

function startCanvas(printer: any, linerless: boolean) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return { canvas, ctx, y: 0, font: 32 }
  const font = Math.round(32 * (1 + (printer?.font_increase_percentage || 0) / 100))
  canvas.width = 576
  canvas.height = 2000
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = '#000000'
  const y = linerless ? 28 : 110
  return { canvas, ctx, y, font }
}

function closeCanvas(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, y: number, bottom: number, linerless: boolean) {
  const result = finishCanvas(canvas, ctx, y + bottom)
  if (linerless) (result as HTMLCanvasElement & { skipLabelFeed?: boolean }).skipLabelFeed = true
  return result
}

function drawCenter(ctx: CanvasRenderingContext2D, y: number, size: number, text: string, bold = false) {
  const value = String(text || '').trim()
  if (!value) return y
  ctx.font = `${bold ? 'bold' : 'normal'} ${size}px Arial`
  ctx.textAlign = 'center'
  const words = value.split(/\s+/)
  let line = ''
  const lines: string[] = []
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word
    if (ctx.measureText(next).width > 520 && line) {
      lines.push(line)
      line = word
    } else {
      line = next
    }
  })
  if (line) lines.push(line)
  lines.forEach((row) => {
    ctx.fillText(row, 288, y)
    y += size * 1.35
  })
  ctx.textAlign = 'left'
  return y
}

function clock(store: any) {
  try {
    return format(utcToZonedTime(new Date(), store?.timeZone || 'UTC'), 'h:mm a')
  } catch {
    return format(new Date(), 'h:mm a')
  }
}

const PX_PER_MM = 576 / 80
const FONT_SIZE: Record<CupLabelFontScale, number> = { compact: 20, regular: 32, large: 48 }

function labelWidthPx(mm: number) {
  const raw = Math.round(Number(mm) * PX_PER_MM)
  return Math.max(8, Math.ceil(raw / 8) * 8)
}

function labelHeightPx(mm: number) {
  return Math.max(32, Math.round(Number(mm) * PX_PER_MM))
}

function steppedFont(scale: CupLabelFontScale) {
  if (scale === 'large') return FONT_SIZE.regular
  if (scale === 'regular') return FONT_SIZE.compact
  return 18
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean)
  if (!words.length) return []
  const lines: string[] = []
  let line = ''
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line)
      line = word
    } else {
      line = next
    }
  })
  if (line) lines.push(line)
  return lines
}

type LabelRow = { text: string; size: number; bold: boolean }

function layoutRows(ctx: CanvasRenderingContext2D, lines: CupLabelLine[], body: number, title: number, maxWidth: number) {
  const rows: LabelRow[] = []
  lines.forEach((line) => {
    const size = line.role === 'title' ? title : body
    ctx.font = `${line.bold ? 'bold' : 'normal'} ${size}px Arial`
    wrapText(ctx, line.text, maxWidth).forEach((text) => rows.push({ text, size, bold: line.bold }))
  })
  return rows
}

function rowsHeight(rows: LabelRow[]) {
  return rows.reduce((sum, row) => sum + row.size * 1.25, 0)
}

function rotateClockwise(source: HTMLCanvasElement) {
  const turned = document.createElement('canvas')
  const ctx = turned.getContext('2d')
  if (!ctx) return source
  turned.width = source.height
  turned.height = source.width
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, turned.width, turned.height)
  ctx.translate(turned.width, 0)
  ctx.rotate(Math.PI / 2)
  ctx.drawImage(source, 0, 0)
  if ((source as HTMLCanvasElement & { skipLabelFeed?: boolean }).skipLabelFeed) {
    (turned as HTMLCanvasElement & { skipLabelFeed?: boolean }).skipLabelFeed = true
  }
  return turned
}

function paintRows(ctx: CanvasRenderingContext2D, rows: LabelRow[], centerX: number, startY: number, limit: number) {
  let y = startY
  for (const row of rows) {
    if (y > limit) break
    ctx.font = `${row.bold ? 'bold' : 'normal'} ${row.size}px Arial`
    ctx.fillText(row.text, centerX, y)
    y += row.size * 1.25
  }
}

export function renderTemplateCupLabel(template: CupLabelTemplate, lines: CupLabelLine[]) {
  const layout = normalizeCupLabelTemplate(template)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return { canvas, overflow: false }
  const receipt = layout.media === 'receipt'
  const width = receipt ? 576 : labelWidthPx(layout.widthMm)
  const padX = receipt ? 28 : 10
  const maxWidth = Math.max(32, width - padX * 2)
  let body = FONT_SIZE[layout.fontScale]
  let title = Math.round(body * 1.35)
  let rows = layoutRows(ctx, lines, body, title, maxWidth)
  let overflow = false
  if (layout.media === 'sticky') {
    const inner = labelHeightPx(layout.heightMm) - 24
    if (rowsHeight(rows) > inner) {
      body = steppedFont(layout.fontScale)
      title = Math.round(body * 1.35)
      rows = layoutRows(ctx, lines, body, title, maxWidth)
      overflow = rowsHeight(rows) > inner
    }
  }
  const content = rowsHeight(rows)
  const top = receipt ? 110 : 12
  const bottom = receipt ? 110 : 12
  const height = layout.media === 'sticky'
    ? labelHeightPx(layout.heightMm)
    : Math.max(layout.media === 'liner' ? labelHeightPx(layout.heightMm) : 32, top + content + bottom)
  canvas.width = width
  canvas.height = height
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, width, height)
  ctx.fillStyle = '#000000'
  ctx.textAlign = 'center'
  const start = top + (rows[0]?.size || body)
  paintRows(ctx, rows, width / 2, start, height - 4)
  if (!receipt) (canvas as HTMLCanvasElement & { skipLabelFeed?: boolean }).skipLabelFeed = true
  return { canvas: layout.rotate90 ? rotateClockwise(canvas) : canvas, overflow }
}

export function renderCupLabels(order: any, store: any, printer: any): HTMLCanvasElement[] {
  if (!order?.cupLabel) return []
  const cups = cupUnits(order.items || [])
  const total = cups.length
  const template = findCupLabelTemplate(store, printer)
  const when = clock(store)
  if (template) {
    return cups.map((item, index) => renderTemplateCupLabel(template, buildCupLabelLines({
      template,
      item,
      order,
      store,
      index,
      total,
      timeText: when,
    })).canvas)
  }
  const linerless = order.cupLabelPaper === 'linerless'
  return cups.map((item, index) => {
    const started = startCanvas(printer, linerless)
    if (!started.ctx) return started.canvas
    let y = started.y
    const nameSize = Math.round(started.font * 1.35)
    y = drawCenter(started.ctx, y, nameSize, order.guestName || '', true)
    y = drawCenter(started.ctx, y + 8, started.font, item.printName || item.name || '')
    optionLines(item, false).forEach((option) => {
      y = drawCenter(started.ctx, y, Math.max(22, started.font - 6), option)
    })
    if (item.itemNote) y = drawCenter(started.ctx, y, Math.max(22, started.font - 6), item.itemNote)
    y = drawCenter(started.ctx, y + 6, Math.max(22, started.font - 6), when)
    y = drawCenter(started.ctx, y, Math.max(22, started.font - 6), `Cup ${index + 1} of ${total}`)
    return closeCanvas(started.canvas, started.ctx, y, linerless ? 20 : 110, linerless)
  })
}

export function renderBagLabel(order: any, _store: any, printer: any): HTMLCanvasElement[] {
  if (!order?.bagLabel) return []
  const started = startCanvas(printer, false)
  if (!started.ctx) return []
  let y = 36
  y = drawCenter(started.ctx, y, Math.round(started.font * 1.35), order.guestName || '', true)
  y = drawCenter(started.ctx, y + 6, started.font, order.orderNumber || '')
  y += drawLine(started.ctx, y)
  y += 16
  started.ctx.font = `normal ${started.font}px Arial`
  started.ctx.textAlign = 'left'
  ;(order.items || []).forEach((item: any) => {
    const qty = String(item.amount ?? item.qty ?? 1)
    started.ctx.fillText(`${qty} ${item.printName || item.name || ''}`, 24, y)
    y += started.font * 1.4
  })
  return [closeCanvas(started.canvas, started.ctx, y, 28, false)]
}

export function renderDeliverySlip(order: any, _store: any, printer: any): HTMLCanvasElement[] {
  if (!order?.deliverySlip || !order?.isDeliveryOrder) return []
  const started = startCanvas(printer, false)
  if (!started.ctx) return []
  let y = 36
  y = drawCenter(started.ctx, y, Math.round(started.font * 1.2), order.orderNumber || '', true)
  y = drawCenter(started.ctx, y + 4, Math.round(started.font * 1.35), guestInitials(order.guestName), true)
  deliveryAddressLines(order.deliveryAddress).forEach((line) => {
    y = drawCenter(started.ctx, y, started.font, line)
  })
  y += drawLine(started.ctx, y)
  y += 20
  ;(order.items || []).forEach((item: any) => {
    const qty = String(item.amount ?? item.qty ?? 1)
    const name = item.printName || item.name || ''
    const note = item.itemNote ? ` [${item.itemNote}]` : ''
    started.ctx.font = `normal ${started.font}px Arial`
    started.ctx.textAlign = 'left'
    started.ctx.fillText(`${qty}  ${name}${note}`, 24, y)
    y += started.font * 1.45
    const optionSize = Math.max(22, started.font - 6)
    optionLines(item, false).forEach((option) => {
      started.ctx.font = `normal ${optionSize}px Arial`
      started.ctx.fillText(`  - ${option}`, 24, y)
      y += optionSize * 1.35
    })
    y += 8
  })
  return [closeCanvas(started.canvas, started.ctx, y, 28, false)]
}
